import { TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useLiveMarket } from "@/lib/useLiveMarket";

const TICKER_CONFIG = [
  { sym: "BTC", name: "Bitcoin", id: "bitcoin", color: "#F7931A", letter: "₿" },
  { sym: "ETH", name: "Ethereum", id: "ethereum", color: "#627EEA", letter: "Ξ" },
  { sym: "BNB", name: "BNB", id: "binancecoin", color: "#F3BA2F", letter: "B" },
  { sym: "SOL", name: "Solana", id: "solana", color: "#9945FF", letter: "◎" },
  { sym: "XRP", name: "XRP", id: "ripple", color: "#23292F", letter: "✕" },
  { sym: "ADA", name: "Cardano", id: "cardano", color: "#0033AD", letter: "₳" },
  { sym: "DOT", name: "Polkadot", id: "polkadot", color: "#E6007A", letter: "●" },
  { sym: "LTC", name: "Litecoin", id: "litecoin", color: "#345D9D", letter: "Ł" },
];

function fmtPrice(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
  return n.toLocaleString("en-US", { maximumFractionDigits: 5 });
}

function fmtPct(pct) {
  if (!Number.isFinite(pct)) return "—";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function TickerItem({ sym, name, color, letter, price, change, up }) {
  return (
    <Link
      to={`/trade/spot/${sym}-USDT`}
      className="group flex items-center gap-2.5 px-5 text-[13px] font-medium shrink-0 hover:bg-cm-blue/[0.04] transition-colors rounded-md"
      data-testid={`ticker-${sym.toLowerCase()}`}
    >
      <span
        className="w-7 h-7 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0 shadow-sm"
        style={{ background: color }}
        aria-hidden
      >
        {letter}
      </span>
      <span className="flex items-baseline gap-1.5 shrink-0">
        <span className="text-cm-navy font-bold tracking-wide">{sym}</span>
        <span className="text-cm-grey text-[11px] font-medium hidden sm:inline">{name}</span>
      </span>
      <span className="text-cm-grey/60 text-[11px] font-semibold">/USDT</span>
      <span className="text-cm-navy font-bold tabular-nums">${price}</span>
      <span
        className={`inline-flex items-center gap-0.5 tabular-nums text-[12px] font-bold ${
          up ? "text-emerald-600" : "text-red-500"
        }`}
      >
        {up ? <TrendingUp size={11} strokeWidth={2.5} /> : <TrendingDown size={11} strokeWidth={2.5} />}
        {change}
      </span>
      <span className="text-[var(--cm-border)] mx-1 hidden sm:inline">|</span>
    </Link>
  );
}

/** Live price strip — sits directly under the site header. */
export default function PriceTicker() {
  const live = useLiveMarket(TICKER_CONFIG.map((t) => t.id));

  const items = TICKER_CONFIG.map((t) => {
    const ticker = live[t.id];
    if (!ticker) {
      return { ...t, price: "—", change: "—", up: true };
    }
    return {
      ...t,
      price: fmtPrice(ticker.price),
      change: fmtPct(ticker.changePct),
      up: ticker.up,
    };
  });

  const track = [...items, ...items];

  return (
    <section
      className="border-b border-[var(--cm-border)] bg-white overflow-hidden"
      data-testid="price-ticker"
      aria-label="Live market prices"
    >
      <div className="flex items-stretch h-[44px]">
        <div className="shrink-0 flex items-center gap-2 px-4 lg:px-5 border-r border-[var(--cm-border)] bg-cm-surface/70">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-cm-navy">Live</span>
        </div>

        <div className="cm-ticker-marquee flex-1 flex items-center">
          <div className="cm-marquee-track flex w-max items-center py-1">
            {track.map((t, i) => (
              <TickerItem key={`${t.sym}-${i}`} {...t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
